/* global tinyMCE */
import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { debounce } from "lodash";
import { remark } from "remark";
import remarkRehype from "remark-rehype";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import rehypeStringify from "rehype-stringify";
import AiAssistant from "@shared/AiAssistant";
import BrivityTextEditor from "@shared/BrivityTextEditor";
import FieldLabel from "@shared/v2/FieldLabel";
import TextInput from "@shared/v2/TextInput";
import Toggle from "@shared/v2/Toggle";
import Typography from "@shared/v2/Typography";
import CollapsibleWebsiteCard from "../shared/CollapsibleWebsiteCard";

const EDITOR_ID = "area-overview-description";

const AreaOverviewPanel = ({ data = undefined, setAreaOverviewData }) => {
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const commitRef = useRef(null);
  useEffect(() => () => commitRef.current?.cancel(), []);

  return (
    <CollapsibleWebsiteCard
      title="Area Overview"
      disabled={!data?.isActive}
      action={
        <Toggle
          checked={data?.isActive}
          onChange={(e) => {
            setAreaOverviewData({
              ...data,
              isActive: e.target.checked,
            });
          }}
        />
      }
    >
      <div className="tw-flex tw-flex-wrap tw-gap-[24px]">
        <div className="tw-flex-1 tw-min-w-[280px] tw-flex tw-flex-col tw-gap-[16px]">
          <Typography variant="body-m" className="tw-font-bold">
            General
          </Typography>
          <TextInput
            autoComplete="off"
            label="Header"
            helperText="Large text shown below the sub-header"
            placeholder="Ex. Boat Enthusiast's Haven"
            value={data?.eyebrow || ""}
            onChange={(e) => {
              setAreaOverviewData({
                ...data,
                eyebrow: e.target.value,
              });
            }}
          />
          <TextInput
            autoComplete="off"
            label="Sub-header"
            helperText="Smaller text shown above the header"
            placeholder="Ex. Life On The Waterfront"
            value={data?.title || ""}
            onChange={(e) => {
              setAreaOverviewData({
                ...data,
                title: e.target.value,
              });
            }}
          />
        </div>
        <div className="tw-flex-1 tw-min-w-[280px] tw-flex tw-flex-col tw-gap-[8px]">
          <div className="tw-flex tw-justify-between tw-items-center tw-gap-[8px]">
            <FieldLabel label="Area Description" isRequired />
            <AiAssistant
              isTinyMce
              textareaId={EDITOR_ID}
              messageType="Generic"
              onInsertClick={(text) => {
                const html = remark()
                  .use([remarkGfm, remarkRehype, rehypeSanitize, rehypeStringify])
                  .processSync(text || "")
                  .toString();
                // Insert into this panel's editor, not whichever one holds the cursor.
                tinyMCE.get(EDITOR_ID).execCommand("mceInsertContent", false, html);
              }}
            />
          </div>
          <BrivityTextEditor
            id={EDITOR_ID}
            value={data?.body || ""}
            settings={{
              min_height: 240,
              plugins: "lists advlist link emoticons",
              setup: (editor) => {
                let initialized = false;
                let userHasEdited = false;
                editor.on("init", () => {
                  initialized = true;
                });
                editor.on("input", () => {
                  userHasEdited = true;
                });
                editor.on("ExecCommand", (e) => {
                  if (e.command === "mceInsertContent") userHasEdited = true;
                });
                const commit = debounce(() => {
                  if (!initialized || !userHasEdited || editor.removed) return;
                  setAreaOverviewData({ ...dataRef.current, body: editor.getContent() });
                }, 500);
                commitRef.current = commit;
                editor.on("input NodeChange", commit);
                editor.on("blur", () => {
                  if (initialized && !editor.removed) flushSync(() => commit.flush());
                });
              },
            }}
          />
        </div>
      </div>
    </CollapsibleWebsiteCard>
  );
};

AreaOverviewPanel.propTypes = {
  data: PropTypes.shape({
    isActive: PropTypes.bool,
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    body: PropTypes.string,
  }),
  setAreaOverviewData: PropTypes.func.isRequired,
};

export default AreaOverviewPanel;
